import { useCallback } from 'react';
import { DataProvider, useDataProvider } from 'react-admin';
import {
    useMutation,
    UseMutationOptions,
    UseMutationResult,
    useQueryClient,
} from 'react-query';
import { updateQueryCacheFromGetTree } from './useGetTree';

import {
    GetTreeParams,
    GetTreeResult,
    TreeDataProvider,
    TreeRecord,
} from '../types';

/**
 * Get a callback to call the dataProvider.getTree() method.
 *
 * Useful for refreshing the tree in an event handler.
 *
 * @param {string} resource The resource name, e.g. 'posts'
 * @param {Params} params The getTree parameters { meta }
 * @param {Object} options Options object to pass to the queryClient.
 * May include side effects to be executed upon success or failure, e.g. { onSuccess: () => { refresh(); } }
 *
 * The data structure is a flat array of TreeRecords (i.e. Records with a children field containing an array of Identifiers).
 *
 * @example // sample return value
 * [
 *   { id: 1, title: 'foo1', children: [3, 4] },
 *   { id: 2, title: 'foo2', children: [] },
 *   { id: 3, title: 'foo3', children: [5] },
 *   { id: 4, title: 'foo4', children: [] },
 *   { id: 5, title: 'foo5', children: [] },
 * ]
 *
 * @example // usage
 *
 * import { useNotify } from 'react-admin';
 * import { useGetTreeCallback } from '@react-admin/ra-tree';
 *
 * const MyComponent = () => {
 *     const refreshTree = useGetTreeCallback(resource);
 *
 *     const handleClick = () => {
 *         refreshTree()
 *             .then(tree => {
 *                 notify(`Reloaded ${tree.length} tree nodes`);
 *             });
 *     };
 *
 *     return <button onClick={handleClick}>Refresh tree</button>;
 * }
 */
export const useGetTreeCallback = <RecordType extends TreeRecord = TreeRecord>(
    resource: string,
    params?: GetTreeParams,
    options?: UseMutationOptions<
        GetTreeResult<RecordType>['data'],
        Error,
        Partial<GetTreeParams>
    >
): UseGetTreeCallbackValue<RecordType> => {
    const dataProvider = useDataProvider<TreeDataProvider & DataProvider>();
    const queryClient = useQueryClient();
    const mutation = useMutation<
        GetTreeResult<RecordType>['data'],
        unknown,
        Partial<UseGetTreeCallbackParams>
    >(
        [resource, 'getTree', params],
        () =>
            dataProvider
                .getTree<RecordType>(resource, params)
                .then(({ data }) => data),
        {
            onSuccess: data => {
                updateQueryCacheFromGetTree(
                    queryClient,
                    resource,
                    data,
                    params
                );
            },
            ...options,
        }
    );

    const callback = useCallback<UseGetTreeCallback<RecordType>>(
        () => mutation.mutateAsync(params),
        [mutation, params]
    );
    return callback;
};

export interface UseGetTreeCallbackParams {
    meta?: any;
}

export type UseGetTreeCallback<RecordType extends TreeRecord = TreeRecord> =
    () => ReturnType<
        UseMutationResult<
            GetTreeResult<RecordType>['data'],
            unknown,
            GetTreeParams
        >['mutateAsync']
    >;

export type UseGetTreeCallbackValue<
    RecordType extends TreeRecord = TreeRecord
> = UseGetTreeCallback<RecordType>;
